import { computed } from 'vue';
import { MixpanelInstance } from '@/plugins/mixpanel/mixpanel';
import useMeQuery from '@/api/queries/me/useMeQuery';
import { useUserDataMutation } from '@/api/mutations/user/useUserDataMutation';
import { ref } from 'vue';
import type { UserData } from '@/api/queries/user/types/user';
import { useUserQuery } from '@/api/queries/user/useUserQuery';

export type VisibilityOption = {
  title: string;
  subtitle: string;
  description: string;
  value: 1 | 2 | 3;
  properties: string[];
  icon: string;
  display: boolean;
};

const REPORT_VISIBILITY_OPTIONS = {
  1: 'private',
  2: 'camouflaged',
  3: 'public',
};

export function usePrivacy() {
  const { auth } = useMeQuery();
  const { refetch, publicUrl } = useUserQuery();

  const privacy = ref(auth.value?.user_data?.profile_visibility);

  const { updateUserData, isLoading } = useUserDataMutation(
    computed(() => ({ profile_visibility: privacy.value })),
  );

  async function savePrivacy(privacyValue: UserData['profile_visibility']) {
    /** REPORT **/
    if (privacyValue) {
      MixpanelInstance.track('privacy_set', {
        candidate_privacy: REPORT_VISIBILITY_OPTIONS[privacyValue],
      });
    }

    privacy.value = privacyValue;

    await updateUserData();
    await refetch();
  }

  const privacyProperties = {
    jobs: 'candidate_profile.share_profile.privacy.properties.jobs',
    sourcing: 'candidate_profile.share_profile.privacy.properties.sourcing',
    results: 'candidate_profile.share_profile.privacy.properties.results',
    share: 'candidate_profile.share_profile.privacy.properties.share',
  };

  const visibilityOptions: VisibilityOption[] = [
    {
      title: 'privacy.public.name',
      subtitle: 'privacy.public.subtitle',
      description: 'privacy.public.definition',
      properties: [],
      value: 3,
      icon: 'fat fa-eye',
      display: true,
    },
    {
      title: 'privacy.private.name',
      subtitle: 'privacy.private.subtitle',
      description: 'privacy.private.definition',
      properties: ['jobs', 'sourcing', 'share', 'results'],
      value: 1,
      icon: 'fat fa-eye-slash',
      display: true,
    },
    {
      title: 'candidate_profile.share_profile.privacy.camouflaged.name',
      subtitle: '',
      description: '',
      properties: [],
      value: 2,
      icon: 'fat fa-eye-slash',
      display: false,
    },
  ];

  // Computes the visibility details depending on the auth visibility value
  const visibilityDetails = computed(() => {
    const storeVisibility = auth.value?.user_data?.profile_visibility;

    return visibilityOptions.find(option => {
      return storeVisibility ? option.value === storeVisibility : option.value === 1;
    }) as VisibilityOption;
  });

  return {
    savePrivacy,
    isLoading,
    visibilityOptions,
    visibilityDetails,
    privacyProperties,
    publicUrl,
  };
}
